import {debounce} from '@github/mini-throttle'
import type {Dispatch, PropsWithChildren} from 'react'
import {createContext, use, useEffect, useMemo, useRef} from 'react'

import {useEntitlement} from '../components/quota/EntitlementContext'
import {useTraceDispatchMaybe} from '../components/tracing/TraceProvider'
import {usePlugins} from '../plugin/ImmersivePluginsProvider'
import {isCopilotPath} from './copilot-chat-helpers'
import {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatAction, CopilotChatState} from './copilot-chat-reducer'
import type {CopilotChatOrg} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {useGetChatState} from './ObservableChatContext'

const CopilotChatManagerContext = createContext<CopilotChatManager | null>(null)

export interface CopilotChatManagerProviderProps {
  apiURL: string
  state: CopilotChatState
  dispatch: Dispatch<CopilotChatAction>
  ssoOrganizations: CopilotChatOrg[]
  realIp?: string
  hasCEorCBAccess?: boolean
  apiVersion?: string
}

export function CopilotChatManagerProvider({
  apiURL,
  state,
  dispatch,
  ssoOrganizations,
  children,
  realIp,
  hasCEorCBAccess,
  apiVersion,
}: PropsWithChildren<CopilotChatManagerProviderProps>) {
  const getChatState = useGetChatState()
  const plugins = usePlugins()
  const traceDispatch = useTraceDispatchMaybe()
  const manager = useMemo(
    () =>
      new CopilotChatManager(
        dispatch,
        apiURL,
        ssoOrganizations,
        getChatState,
        realIp,
        undefined,
        hasCEorCBAccess,
        plugins,
        apiVersion,
        traceDispatch,
      ),
    [dispatch, apiURL, ssoOrganizations, getChatState, realIp, hasCEorCBAccess, plugins, apiVersion, traceDispatch],
  )
  const {licenseType} = useEntitlement()

  const selectedThreadIDRef = useRef(state.selectedThreadID)
  // Store the original (unwrapped) history methods so re-runs don't wrap already-wrapped versions
  const originalHistoryMethodsRef = useRef<{
    pushState: History['pushState']
    replaceState: History['replaceState']
  } | null>(null)
  useEffect(() => {
    selectedThreadIDRef.current = state.selectedThreadID
  })

  useEffect(() => {
    const controller = new AbortController()

    // Lazily capture original history methods once (SSR-safe)
    if (!originalHistoryMethodsRef.current) {
      originalHistoryMethodsRef.current = {
        // eslint-disable-next-line @typescript-eslint/unbound-method, no-restricted-properties
        pushState: window.history.pushState,
        // eslint-disable-next-line @typescript-eslint/unbound-method, no-restricted-properties
        replaceState: window.history.replaceState,
      }
    }
    const originals = originalHistoryMethodsRef.current

    const fetchContextPage = async () => {
      // Only fetch implicit context for new threads — skip if the user is in an existing thread
      if (selectedThreadIDRef.current) {
        return
      }

      const hash = window.location.hash
      const pathName = window.location.pathname
      const url = window.location.hash ? `${pathName}${hash}` : pathName

      // Skip copilot internal URLs — these are chat navigation routes (e.g. /copilot/c/{threadId}),
      // not actual page context. Implicit context should only be fetched for host pages.
      if (isCopilotPath(pathName)) {
        return
      }

      const urlParts = url.slice(1).split('/')
      const owner = urlParts[0]
      const repo = urlParts[1]

      if (owner && repo) {
        await manager.fetchImplicitContext(url, owner, repo, true)
      } else if (copilotFeatureFlags.immersiveEmbeddedImplicitReferences) {
        await manager.fetchImplicitContext(url, '', '', true)
      }
    }

    const voidFetch = () => {
      void fetchContextPage()
    }

    const debouncedFetch = debounce(() => voidFetch(), 500)

    function watchHistoryEvents() {
      const {pushState, replaceState} = originals

      // eslint-disable-next-line no-restricted-properties
      window.history.replaceState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        replaceState.apply(window.history, args)
        window.dispatchEvent(new Event('replaceState'))
      }

      // pushState is called by the browser when the user navigates to a new page. We need to listen
      // for this event to clear and generates suggestions.
      // eslint-disable-next-line no-restricted-properties
      window.history.pushState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        pushState.apply(window.history, args)
        window.dispatchEvent(new Event('pushState'))
      }

      window.addEventListener('popstate', debouncedFetch, {signal: controller.signal})
      window.addEventListener('replaceState', debouncedFetch, {signal: controller.signal})
      window.addEventListener('pushState', debouncedFetch, {signal: controller.signal})
    }

    const shouldFetchImplicitReferences = copilotFeatureFlags.immersiveEmbeddedImplicitReferences

    if (state.chatIsOpen) {
      if (!state.skipImplicitReferences) {
        voidFetch()
      }
      watchHistoryEvents()
    } else if (shouldFetchImplicitReferences) {
      watchHistoryEvents()
    }
    return () => {
      controller.abort()
      // Restore original history methods to prevent stacking wrappers on re-runs
      const {pushState, replaceState} = originals
      // eslint-disable-next-line no-restricted-properties
      window.history.pushState = pushState
      // eslint-disable-next-line no-restricted-properties
      window.history.replaceState = replaceState
    }
  }, [manager, state.chatIsOpen, state.skipImplicitReferences, state.mode, licenseType])

  return <CopilotChatManagerContext value={manager}>{children}</CopilotChatManagerContext>
}

export function useChatManager() {
  const context = use(CopilotChatManagerContext)
  if (!context) {
    throw new Error('useChatManager must be used within a CopilotChatManagerProvider')
  }
  return context
}
